import { fetchPowerOutageDataForWards, fetchGroundwaterDataForWards } from '../services/api';
import { calculateSeverityLevel } from './severityUtils';

/**
 * Fetch ward severity distribution for an indicator
 * Supports power outages and groundwater stress levels. Other indicators will return null.
 * 
 * @param {string} indicatorName - Name of the indicator
 * @param {string} city - City name
 * @param {number} threshold - Threshold value for severity calculation
 * @param {Date} startDate - Start date for data range (defaults to current month)
 * @param {Date} endDate - End date for data range (defaults to current month)
 * @returns {Promise<{LOW: number, MEDIUM: number, HIGH: number, criticalMappedToHigh?: number, totalWards: number}>|null}
 */
export const fetchWardSeverityDistribution = async (
  indicatorName,
  city,
  threshold,
  startDate = null,
  endDate = null
) => {
  // Currently only power outages have ward-level severity API
  if (indicatorName === 'Power outages (duration and geographic scope)' || indicatorName === 'Power Outage') {
    try {
      // Use May 2025 as default if dates not provided (matches demo data)
      if (!startDate || !endDate) {
        // Default to May 2025 where demo data exists
        startDate = '2025-05-01';
        endDate = '2025-05-31';
        console.log(`[STATUS] 📅 Using default date range: May 2025 (demo data)`);
      } else {
        // Convert Date objects to strings if needed
        if (startDate instanceof Date) {
          const year = startDate.getFullYear();
          const month = startDate.getMonth() + 1;
          startDate = `${year}-${String(month).padStart(2, '0')}-01`;
        }
        if (endDate instanceof Date) {
          const year = endDate.getFullYear();
          const month = endDate.getMonth() + 1;
          const lastDay = new Date(year, month, 0).getDate();
          endDate = `${year}-${String(month).padStart(2, '0')}-${lastDay}`;
        }
      }

      console.log(`[STATUS] 🌐 Fetching ward severity distribution for: ${indicatorName} | City: ${city} | Threshold: ${threshold} | Date Range: ${startDate} to ${endDate}`);
      const wardData = await fetchPowerOutageDataForWards(startDate, endDate, threshold, city);
      
      if (!wardData || Object.keys(wardData).length === 0) {
        console.warn(`[STATUS] ⚠️  No ward data returned for: ${indicatorName} | City: ${city}`);
        return null;
      }

      // Aggregate severity counts across ONLY wards with available data
      const severityCounts = { LOW: 0, MEDIUM: 0, HIGH: 0 };
      let criticalMappedToHigh = 0;
      let wardsWithData = 0; // Count only wards that have valid severity data

      Object.keys(wardData).forEach(ward => {
        const wardSeverity = wardData[ward].dominant_severity;
        if (!wardSeverity) return;

        const normalizedSeverity = String(wardSeverity).toUpperCase();
        if (normalizedSeverity === 'CRITICAL') {
          // Fold CRITICAL into HIGH so UI only shows three levels
          severityCounts.HIGH++;
          criticalMappedToHigh++;
          wardsWithData++;
        } else if (severityCounts.hasOwnProperty(normalizedSeverity)) {
          severityCounts[normalizedSeverity]++;
          wardsWithData++;
        }
      });

      // Ensure we only calculate percentages based on wards with data
      const totalWards = wardsWithData;

      if (totalWards === 0) {
        console.warn(`[STATUS] ⚠️  No wards with valid severity data for: ${indicatorName} | City: ${city}`);
        return null;
      }

      const distribution = {
        ...severityCounts,
        criticalMappedToHigh,
        totalWards
      };
      console.log(`[STATUS] ✅ Ward severity distribution calculated for: ${indicatorName} | Total Wards with Data: ${totalWards} | LOW: ${severityCounts.LOW}, MEDIUM: ${severityCounts.MEDIUM}, HIGH: ${severityCounts.HIGH} (mapped critical=${criticalMappedToHigh})`);
      console.log(`[STATUS] 📊 Percentages: L: ${((severityCounts.LOW / totalWards) * 100).toFixed(1)}%, M: ${((severityCounts.MEDIUM / totalWards) * 100).toFixed(1)}%, H: ${((severityCounts.HIGH / totalWards) * 100).toFixed(1)}%`);
      return distribution;
    } catch (error) {
      console.error(`[STATUS] 🔴 Error fetching ward severity distribution for ${indicatorName}:`, error.message);
      return null;
    }
  }

  // Groundwater stress levels: use groundwater API and aggregate by dominant_severity
  if (indicatorName === 'Groundwater stress levels') {
    try {
      let startStr = startDate;
      let endStr = endDate;
      if (!startStr || !endStr) {
        const now = new Date();
        startStr = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-01`;
        const lastDay = new Date(now.getFullYear(), now.getMonth() + 1, 0).getDate();
        endStr = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${lastDay}`;
      } else {
        if (startStr instanceof Date) {
          const y = startStr.getFullYear(), m = startStr.getMonth() + 1;
          startStr = `${y}-${String(m).padStart(2, '0')}-01`;
        }
        if (endStr instanceof Date) {
          const y = endStr.getFullYear(), m = endStr.getMonth() + 1;
          const lastDay = new Date(y, m, 0).getDate();
          endStr = `${y}-${String(m).padStart(2, '0')}-${lastDay}`;
        }
      }
      const wardData = await fetchGroundwaterDataForWards(startStr, endStr, threshold, city);
      if (!wardData || Object.keys(wardData).length === 0) {
        console.warn(`[STATUS] ⚠️  No groundwater ward data for: ${indicatorName} | City: ${city}`);
        return null;
      }
      const severityCounts = { LOW: 0, MEDIUM: 0, HIGH: 0 };
      let wardsWithData = 0;
      Object.keys(wardData).forEach(ward => {
        const wardSeverity = wardData[ward].dominant_severity;
        if (!wardSeverity) return;
        const normalizedSeverity = String(wardSeverity).toUpperCase();
        if (severityCounts.hasOwnProperty(normalizedSeverity)) {
          severityCounts[normalizedSeverity]++;
          wardsWithData++;
        }
      });
      const totalWards = wardsWithData;
      if (totalWards === 0) return null;
      const distribution = { ...severityCounts, totalWards };
      console.log(`[STATUS] ✅ Groundwater severity distribution: ${indicatorName} | Wards: ${totalWards} | L: ${severityCounts.LOW} M: ${severityCounts.MEDIUM} H: ${severityCounts.HIGH}`);
      return distribution;
    } catch (error) {
      console.error(`[STATUS] 🔴 Error fetching groundwater severity for ${indicatorName}:`, error.message);
      return null;
    }
  }

  // For other indicators, return null (no ward-level data available yet)
  console.log(`[STATUS] ℹ️  No API endpoint available for: ${indicatorName} | City: ${city}`);
  return null;
};

/**
 * Calculate indicator status (Normal, Warning, High) based on ward severity distribution
 * 
 * Classification Rules (Mutually Exclusive):
 * - High: H% > 85%
 * - Warning: H% ≤ 85% AND M% > 60%
 * - Normal: H% ≤ 85% AND M% ≤ 60% AND L% > 85%
 * 
 * @param {Object} severityDistribution - Object with LOW, MEDIUM, HIGH counts and totalWards
 * @returns {string|null} - 'normal', 'warning', 'high', or null if no data
 */
export const calculateIndicatorStatus = (severityDistribution) => {
  if (!severityDistribution || !severityDistribution.totalWards || severityDistribution.totalWards === 0) {
    return null;
  }

  const { LOW = 0, MEDIUM = 0, HIGH = 0, CRITICAL = 0, totalWards } = severityDistribution;

  // Ensure totalWards only includes wards with data (should already be the case, but double-check)
  const wardsWithData = LOW + MEDIUM + HIGH + CRITICAL;
  if (wardsWithData === 0) {
    console.warn(`[STATUS] ⚠️  No wards with severity data in distribution`);
    return null;
  }

  // Calculate percentages based ONLY on wards with available data
  const LPercent = (LOW / totalWards) * 100;
  const MPercent = (MEDIUM / totalWards) * 100;
  const HPercent = ((HIGH + CRITICAL) / totalWards) * 100; // CRITICAL folded into HIGH

  // Verify percentages sum to 100% (within rounding error)
  const totalPercent = LPercent + MPercent + HPercent;
  if (Math.abs(totalPercent - 100) > 0.1) {
    console.warn(`[STATUS] ⚠️  Percentages don't sum to 100%: ${totalPercent.toFixed(2)}% | L: ${LPercent.toFixed(1)}%, M: ${MPercent.toFixed(1)}%, H: ${HPercent.toFixed(1)}% (CRITICAL mapped into H)`);
  }

  // Classification Rules (Mutually Exclusive)
  // High: H% > 85%
  if (HPercent > 85) {
    console.log(`[STATUS] 🔴 HIGH | L: ${LPercent.toFixed(1)}% M: ${MPercent.toFixed(1)}% H: ${HPercent.toFixed(1)}% | H=${HPercent.toFixed(1)}% > 85%`);
    return 'high';
  }

  // Warning: H% ≤ 85% AND M% > 60%
  if (HPercent <= 85 && MPercent > 60) {
    console.log(`[STATUS] 🟠 WARNING | L: ${LPercent.toFixed(1)}% M: ${MPercent.toFixed(1)}% H: ${HPercent.toFixed(1)}% | H=${HPercent.toFixed(1)}% ≤ 85% AND M=${MPercent.toFixed(1)}% > 60%`);
    return 'warning';
  }

  // Normal: H% ≤ 85% AND M% ≤ 60% AND L% > 85%
  if (HPercent <= 85 && MPercent <= 60 && LPercent > 85) {
    console.log(`[STATUS] 🟢 NORMAL | L: ${LPercent.toFixed(1)}% M: ${MPercent.toFixed(1)}% H: ${HPercent.toFixed(1)}% | H=${HPercent.toFixed(1)}% ≤ 85% AND M=${MPercent.toFixed(1)}% ≤ 60% AND L=${LPercent.toFixed(1)}% > 85%`);
    return 'normal';
  }

  // Default to warning if none of the above conditions are met
  // (This handles edge cases where distribution doesn't fit cleanly into categories)
  console.log(`[STATUS] 🟠 WARNING (default) | L: ${LPercent.toFixed(1)}% M: ${MPercent.toFixed(1)}% H: ${HPercent.toFixed(1)}% | No specific rule matched`);
  return 'warning';
};

